/**
 * `/` — the lobby. The first screen anybody sees.
 *
 * Two jobs, and the order they are in is the design:
 *
 *   1. Show you your own camera and let you fix it. Everybody wants to know
 *      what they look like before they appear to other people, and a lobby
 *      that skips this is the reason people join calls apologising.
 *   2. Get you into a room — either a new one, or one somebody gave you a code
 *      for.
 *
 * The name and the devices are shared between both paths, so the form is one
 * card with a segmented control rather than two competing cards. Two cards
 * makes the page ask "which of these am I?" before it asks anything useful.
 *
 * Nothing here imports `livekit-client`. The preview is plain `getUserMedia`
 * (`features/call/use-preview.ts`), and its live tracks are handed to the call
 * so that joining does not restart the camera.
 *
 * ON A PHONE this screen is a fixed frame, not a document. It is sized to
 * exactly one viewport and nothing in it scrolls, so the camera, your name and
 * the button that gets you into a room are all reachable without a swipe. Three
 * things pay for that, and each is a deliberate trade rather than a tidy-up:
 *
 *   - the create-call settings (title, room size, what is kept) collapse behind
 *     a summary row that states what they currently are. Nothing is removed —
 *     "2 people · audio kept · video off" is on screen either way — and at `sm`
 *     and up the disclosure does not exist at all. The video clause was added
 *     to that line rather than to the card, so the retention choice cost this
 *     screen no permanent height; the only thing that grows is the panel that
 *     was already taller than the viewport when open.
 *   - the privacy card is `sm` and up only. It is not lost: the shell's footer
 *     carries the same sentence on mobile, and repeating it would cost ~22% of
 *     an iPhone SE viewport to say a thing already said.
 *   - the page header is written twice — a compact version below `sm`, the
 *     shared `PageHeader` above it — rather than one responsive block. The
 *     shared component is not this screen's to reshape, and the desktop header
 *     is exactly what the operator has today.
 *
 * The arithmetic that has to hold is: 667 (iPhone SE) − the tab bar − the
 * shell's padding leaves 563px, and the compact header, the 16:9 preview with
 * its device row, and the form card add up to less than that. It is measured,
 * not assumed; see FINDINGS #24.
 */

import * as React from 'react'
import { useNavigate } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'
import {
  ArrowRight,
  ChevronDown,
  Loader2,
  ShieldCheck,
  Sparkles,
  Users,
  Waves,
} from 'lucide-react'
import {
  normalizeJoinCode,
  PARTICIPANT_LIMITS,
  VIDEO_RECORDING_MODES,
  videoModeRequiresFocusIdentity,
  type AppointmentGrant,
  type HealthResponse,
  type JoinGrant,
  type ParticipantLimit,
  type RecordingSelection,
  type RecordingVideoSelection,
  type VideoRecordingMode,
} from '@era/shared'
import { PageHeader } from '@/components/page-header'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent } from '@/components/ui/card'
import { Field, Input, Label } from '@/components/ui/input'
import { MediaPreview } from '@/features/call/components/media-preview'
// The lobby and the mid-call control name the modes with the SAME words. A
// second list here would drift, and the drift would read as two different
// features. `video-control.ts` is pure policy logic — it imports no
// `livekit-client` — so this does not break the rule at the top of this file.
import { videoModeLabel } from '@/features/call/video-control'
import { usePreview } from '@/features/call/use-preview'
import { ApiError, api } from '@/lib/api'
import { AddToHomeScreenHint } from '@/lib/immersive-call'
import { recallName, rememberName, stashGrant, stashHostToken } from '@/lib/livekit-handoff'
import { keys } from '@/lib/query'
import { cn } from '@/lib/utils'

type Mode = 'create' | 'join'

interface Failure {
  title: string
  detail: string
  /** Drives the auto-retry countdown. Only a 503 sets it. */
  retryable: boolean
  /**
   * The failure was caused by the retained-video choice, and the same request
   * without it would have gone through. Offers the one-tap way out.
   */
  videoCaused?: boolean
}

/** How many times a "no node had capacity" is retried before we stop and ask. */
const MAX_CAPACITY_RETRIES = 3

/**
 * What each retained-video mode ACTUALLY does, in one line, next to the picker.
 *
 * The differences between these five are invisible until playback, and that was
 * the strongest argument against ever putting them in a lobby. It is a writing
 * problem, so it is answered in writing: no mode is offered without its real
 * behaviour stated beside it, including the numbers (1.5 s, three or more) that
 * decide what the finished file looks like.
 *
 * A `Record` over the full mode union rather than the four this screen offers,
 * so a mode added upstream is a COMPILE error here — the alternative is a
 * picker that silently gains an option nobody has described.
 */
const VIDEO_MODE_DETAIL: Record<VideoRecordingMode, string> = {
  none: 'Nothing is kept. Everyone still sees each other normally — this is only about what survives the call.',
  auto: 'One fixed camera in a two-person call; whoever is speaking once there are three or more.',
  active_speaker: 'Follows whoever is speaking, one person at a time, switching after 1.5 seconds.',
  meeting_view: 'Both cameras, side by side.',
  focused_guest:
    'Keeps one named person’s camera and never cuts to whoever else is speaking. Chosen during the call: it names a participant, and nobody has joined yet.',
}

/**
 * The same five, compressed to fit the collapsed summary row on a phone.
 *
 * `video off` — the DEFAULT — is the one that has to survive at 375 px, and it
 * does: the full line "2 people · audio kept · video off" measures ~174 px into
 * ~190 px of space. The on-modes are longer and may ellipsize on the narrowest
 * device, which is the safe direction to lose a word in: a truncated "video
 * speak…" still reads as on, whereas a truncated "video off" would read as
 * unknown. That is why the words for on are short rather than descriptive.
 */
const VIDEO_MODE_SUMMARY: Record<VideoRecordingMode, string> = {
  none: 'video off',
  auto: 'video auto',
  active_speaker: 'video speaker',
  meeting_view: 'video both',
  focused_guest: 'video one guest',
}

/**
 * The modes a room can be CREATED with, derived rather than listed.
 *
 * The filter is `videoModeRequiresFocusIdentity`, not `!== 'focused_guest'`.
 * The rule is "a mode that needs a participant identity cannot be chosen before
 * any participant exists", and expressing it as the rule means a future mode
 * with the same requirement is excluded automatically instead of shipping a
 * picker that produces a guaranteed 422.
 *
 * Excluded modes are still RENDERED — disabled, with their real behaviour and
 * where to find them — because a picker that silently omits an option teaches
 * people the option does not exist.
 */
type CreationVideoView = RecordingVideoSelection['view']
const CREATION_VIDEO_MODES = VIDEO_RECORDING_MODES.filter(
  (mode): mode is CreationVideoView => mode !== 'none' && !videoModeRequiresFocusIdentity(mode),
)
const CREATION_VIDEO_VIEW_OPTIONS = VIDEO_RECORDING_MODES.filter(
  (mode): mode is CreationVideoView => mode !== 'none',
)

/**
 * The full spec, always sent, even for `none`.
 *
 * Omitting it would let the BFF's creation default decide, and the whole point
 * of this control is that the answer is the human's. If that default ever moved
 * off `none`, a lobby that omitted the field would quietly start keeping video
 * for people who had explicitly chosen not to. So "off" is stated, not implied.
 *
 * `focus_identity` is null and cannot be anything else here: no identity exists
 * at creation, and the BFF refuses the field outright on this route.
 */
function creationRecording(
  mode: RecordingSelection['mode'],
  view: CreationVideoView,
): RecordingSelection {
  if (mode !== 'video') return { mode, video: null }
  return {
    mode: 'video',
    video: { view, focus_identity: null, screen_share: 'replace', resolution: '720p' },
  }
}

export function HomeRoute() {
  const navigate = useNavigate()
  const preview = usePreview()

  const [mode, setMode] = React.useState<Mode>('create')
  const [name, setName] = React.useState(recallName)
  const [title, setTitle] = React.useState('')
  const [participantLimit, setParticipantLimit] = React.useState<ParticipantLimit>(2)
  const [recordingMode, setRecordingMode] = React.useState<RecordingSelection['mode']>('none')
  // Default OFF, and that is the product decision, not an initial value waiting
  // to be improved. The full argument is on `DEFAULT_CREATION_VIDEO_RECORDING`
  // in `apps/bff/src/services/rooms.ts`; the short of it is that the two
  // mistakes are not symmetric — a call that should have been filmed can be
  // held again, one that should not have been cannot be un-filmed.
  const [videoMode, setVideoMode] = React.useState<CreationVideoView>('auto')
  const [code, setCode] = React.useState('')

  /**
   * Phone only. At `sm` and up the settings are always rendered and this is
   * never read — which is why the class below is `hidden sm:block` rather than
   * a conditional render: the fields must exist in the DOM at every width, so
   * that a value chosen on a phone survives a rotation into the wide layout.
   */
  const [optionsOpen, setOptionsOpen] = React.useState(false)

  /**
   * Opening the options is the one thing on this screen that makes it taller
   * than the viewport, so it is also the one thing that has to bring the
   * primary button back with it. `scrollIntoView` walks up to the nearest
   * scrollable ancestor, which is the shell's `<main>` — this screen does not
   * need to know that, and must not, because the scroller is the frame's.
   */
  const submitRef = React.useRef<HTMLButtonElement>(null)
  React.useEffect(() => {
    if (!optionsOpen) return
    submitRef.current?.scrollIntoView({ block: 'end', behavior: 'smooth' })
  }, [optionsOpen])

  const [busy, setBusy] = React.useState(false)
  const [failure, setFailure] = React.useState<Failure | null>(null)
  const [countdown, setCountdown] = React.useState<number | null>(null)
  const attempts = React.useRef(0)

  const health = useQuery({
    queryKey: keys.health,
    queryFn: () => api<HealthResponse>('/api/health'),
    staleTime: 30_000,
  })

  const trimmedName = name.trim()
  const normalizedCode = normalizeJoinCode(extractCode(code))

  /* ---------------------------------------------------------------------- */

  /**
   * Both paths end the same way, on purpose: stash the grant, hand the camera
   * over, then navigate. `url` and `token` travel together inside `JoinGrant`
   * and are never stored apart — the failure that causes looks like an auth
   * bug and is not one.
   */
  const enter = React.useCallback(
    (grant: JoinGrant) => {
      rememberName(trimmedName)
      stashGrant({ grant })
      preview.handOff()
      void navigate({ to: '/call/$code', params: { code: grant.join_code } })
    },
    [navigate, preview, trimmedName],
  )

  /**
   * `videoOverride` exists for exactly one caller: "Start without video" on a
   * `no_video_recorder_capacity` failure. It cannot go through `setVideoMode`
   * and then call this, because the state update is not visible to the closure
   * this render already built — the retry would send the mode that just failed.
   * The state is set as well, so the picker agrees with what was sent.
   */
  const createRoom = React.useCallback(async (recordingOverride?: RecordingSelection['mode']) => {
    const requestedMode = recordingOverride ?? recordingMode
    setBusy(true)
    setFailure(null)
    setCountdown(null)
    try {
      const grant = await api<JoinGrant>('/api/rooms', {
        method: 'POST',
        body: {
          display_name: trimmedName,
          title: title.trim() || undefined,
          participant_limit: participantLimit,
          recording: creationRecording(requestedMode, videoMode),
        },
      })
      attempts.current = 0
      enter(grant)
    } catch (cause) {
      const described = describeCreateFailure(cause, participantLimit, videoMode)
      setFailure(described)
      // Two different 503s reach here and both are worth retrying by
      // themselves: no media node had capacity for the call, or no RECORDER was
      // free to keep the video that was asked for. Neither may be answered by
      // inventing a placement — the URL must be the node that actually holds
      // the room — and the second may not be answered by quietly dropping the
      // video either. Creation fails atomically, so a retry starts clean.
      if (described.retryable && attempts.current < MAX_CAPACITY_RETRIES) {
        attempts.current += 1
        setCountdown(3)
      }
    } finally {
      setBusy(false)
    }
  }, [enter, participantLimit, recordingMode, title, trimmedName, videoMode])

  /**
   * The other way to start a call: publish a link now, start it later.
   *
   * This is the host-gated path from the handoff spec, and it differs from
   * `createRoom` above in the one way that matters — it creates NOTHING
   * upstream. No session, no capacity unit, no recorder reservation. It writes
   * a row and hands back a URL, so the link can go into a calendar invite days
   * before anybody is entitled to a room.
   *
   * The host token comes back exactly once and is put in this tab's
   * sessionStorage, never in the URL. Closing the tab means starting that
   * meeting is no longer possible from this browser, which is the correct
   * trade for a capability that spends capacity — see `stashHostToken`.
   */
  const createAppointment = React.useCallback(async () => {
    setBusy(true)
    setFailure(null)
    setCountdown(null)
    try {
      const appointment = await api<AppointmentGrant>('/api/appointments', {
        method: 'POST',
        body: {
          display_name: trimmedName,
          title: title.trim() || undefined,
          participant_limit: participantLimit,
          recording: creationRecording(recordingMode, videoMode),
        },
      })
      rememberName(trimmedName)
      stashHostToken(appointment.join_slug, appointment.host_token)
      void navigate({ to: '/j/$slug', params: { slug: appointment.join_slug } })
    } catch (cause) {
      setFailure(describeCreateFailure(cause, participantLimit, videoMode))
    } finally {
      setBusy(false)
    }
  }, [navigate, participantLimit, recordingMode, title, trimmedName, videoMode])

  const joinRoom = React.useCallback(async () => {
    if (!normalizedCode) return
    setBusy(true)
    setFailure(null)
    try {
      const grant = await api<JoinGrant>(`/api/rooms/${encodeURIComponent(normalizedCode)}/join`, {
        method: 'POST',
        body: { display_name: trimmedName },
      })
      enter(grant)
    } catch (cause) {
      setFailure(describeJoinFailure(cause))
    } finally {
      setBusy(false)
    }
  }, [enter, normalizedCode, trimmedName])

  // The retry countdown. Rendered as a number rather than as a spinner so that
  // "we are waiting on purpose" is distinguishable from "this is stuck".
  //
  // `createRoom` goes through a ref: it closes over the preview, whose identity
  // changes on every render, so listing it as a dependency would clear and
  // recreate the timeout on each render and the countdown would never land.
  const createRef = React.useRef(createRoom)
  createRef.current = createRoom
  React.useEffect(() => {
    if (countdown === null) return
    if (countdown <= 0) {
      setCountdown(null)
      void createRef.current()
      return
    }
    const timer = window.setTimeout(() => setCountdown(countdown - 1), 1000)
    return () => window.clearTimeout(timer)
  }, [countdown])

  const canSubmit =
    trimmedName.length > 0 && !busy && (mode === 'create' || normalizedCode !== null)

  const submit = (event: React.FormEvent) => {
    event.preventDefault()
    if (!canSubmit) return
    void (mode === 'create' ? createRoom() : joinRoom())
  }

  /* ---------------------------------------------------------------------- */

  return (
    <div
      className={cn(
        /* PHONE: exactly one viewport, with the shell's own chrome subtracted
           from it BY NAME rather than by a guessed number. `--tabbar-h`, `--sat`
           and `--sab` are `styles.css`'s tokens; the 3rem is the shell's
           `py-6`. A magic 56px here would be right on one build of the shell
           and silently wrong on the next.

           `100dvh` for the same reason the shell uses it: `100vh` on iOS is the
           EXPANDED viewport, so a `100vh` version of this line is taller than
           the screen for as long as the URL bar is showing.

           At `sm` and up this is the original `space-y-10` document flow, and
           the height cap is gone — the wide layout is allowed to be as tall as
           it likes, because there the page scrolls.

           `min-h`, NOT `h`. It was a fixed height, which is right for the
           closed state and wrong the moment anything grows: opening Options
           made the card taller than the frame, and with no overflow rule the
           overflow simply painted — straight through the shell footer, over
           "Room size" and the 2/4/8 buttons, on every phone size. A fixed
           height says "be exactly one viewport" when the intent was "fill at
           least one viewport", and the difference only shows up when the
           content wins.

           `min-h` keeps every measurement the closed layout was designed
           against (nothing scrolls, everything is reachable) and lets the
           shell's `main` — which is already `overflow-y-auto` — take the
           growth when the panel opens. */
        'flex min-h-[calc(100dvh_-_var(--sat)_-_var(--sab)_-_var(--tabbar-h)_-_3rem)] flex-col gap-2',
        'sm:block sm:min-h-0 sm:h-auto sm:space-y-10',
      )}
    >
      {/* The phone header. Title only, and the health pill only when it is bad
          news: "server ready" is the default state and does not need to hold a
          line on the tightest screen in the range, whereas "unreachable"
          explains why the button below is about to fail.

          The lede is gated on VIEWPORT HEIGHT, not width. Every phone in scope
          is the same handful of widths, and the thing that actually differs is
          how much room is left after the tab bar — an iPhone SE has 563px and
          an iPhone 15 Pro Max has 733px. So the sentence appears exactly where
          there is space for it rather than being cut everywhere or nowhere. */}
      <div className="shrink-0 sm:hidden">
        <div className="flex items-center justify-between gap-3">
          <h1 className="display text-2xl leading-tight text-ink">Start a conversation.</h1>
          {health.isError ? (
            <Badge tone="critical" className="shrink-0">
              offline
            </Badge>
          ) : null}
        </div>
        <p className="mt-1.5 hidden text-[0.8125rem] leading-relaxed text-ink-muted [@media(min-height:720px)]:block">
          A normal video call. Afterwards you keep the part that is worth keeping: per-speaker
          audio and a word-timed transcript.
        </p>
      </div>

      <div className="hidden sm:block">
        <PageHeader
          title="Start a conversation."
          lede="A normal video call — camera, screen share, the lot. Afterwards you keep the part that is worth keeping: per-speaker audio, a word-timed transcript, and what the conversation actually looked like."
          actions={
            health.data ? (
              <Badge tone="ok" title={`${health.data.database.rooms} rooms in the local catalog`}>
                <ShieldCheck className="size-3.5" />
                server ready
              </Badge>
            ) : health.isError ? (
              <Badge tone="critical">server unreachable</Badge>
            ) : null
          }
        />
      </div>

      <div className="flex min-h-0 flex-1 flex-col gap-2 sm:grid sm:gap-8 lg:grid-cols-[1.1fr_1fr] lg:items-start">
        {/* `grow shrink-0`, not `flex-1`. Grow so a tall phone spends its extra
            room here — the camera is the thing worth more of the screen —
            `justify-center` so that room lands around the preview rather than
            below it. Shrink-0 so that on a viewport too short for everything
            the block keeps its intrinsic height and the page scrolls, instead
            of the frame quietly squashing the video out of its aspect ratio. */}
        <div className="flex shrink-0 grow flex-col justify-center gap-2 sm:block sm:space-y-3">
          <MediaPreview
            preview={preview}
            displayName={trimmedName || 'You'}
            compact
          />
          <p className="hidden text-xs leading-relaxed text-ink-dim sm:block">
            This is what the others will see. Whatever you set here — camera off, a different
            microphone — is what you join with, and the camera stays open the whole way in.
          </p>
        </div>

        <div className="shrink-0 space-y-5">
          <Card>
            <CardContent className="space-y-2 px-4 py-2.5 sm:space-y-6 sm:px-6 sm:pt-6 sm:pb-6">
              <Field label="Your name" hint="Shown to the others">
                <Input
                  value={name}
                  onChange={(event) => setName(event.target.value)}
                  placeholder="Ana Ruiz"
                  autoComplete="name"
                  maxLength={64}
                  className="h-11"
                />
              </Field>

              <Segmented
                label="Start a new call or join one with a code"
                value={mode}
                onChange={(next) => {
                  setMode(next)
                  setFailure(null)
                  setCountdown(null)
                }}
                options={[
                  { value: 'create', label: 'New call' },
                  { value: 'join', label: 'Join with a code' },
                ]}
              />

              <form onSubmit={submit} className="space-y-2 sm:space-y-5">
                {mode === 'create' ? (
                  <>
                    {/* Phone only. It is a summary as much as a control: the
                        settings it hides are stated on its own face, so the
                        collapsed state still answers "what am I about to
                        create?" without being opened — and both retention
                        channels are named there, because "video off" is the
                        answer somebody scanning this row is looking for. */}
                    <button
                      type="button"
                      onClick={() => setOptionsOpen((open) => !open)}
                      aria-expanded={optionsOpen}
                      aria-controls="call-options"
                      className={cn(
                        'tap44 flex h-10 w-full items-center gap-2 rounded-lg border border-line bg-surface-2/70 px-3',
                        'text-[0.8125rem] transition-colors active:bg-surface-3 sm:hidden',
                      )}
                    >
                      {/* "Options", not "Call options", and the word was bought
                          rather than saved. MEASURED at 375px: the row gives
                          283px of content, "Call options" took 72 of it and the
                          three-clause summary needs 199 — so the summary was
                          clipped by 20px and the clause it lost was the tail,
                          "video off". Losing THAT word to an ellipsis is the
                          one truncation this row must not produce, because a
                          privacy summary that trails off reads as unknown. The
                          shorter label measures 48px and frees 24, which lands
                          the full 199px line in 203px of space — 4px of slack,
                          so a longer word here costs the tail again. An ON mode
                          ("video speaker") still ellipsizes at 375px, and that
                          is the safe direction to lose a word: a truncated
                          "video speak…" still reads as on. The control is in a form
                          headed "New call", so nothing is lost by not repeating
                          the word. */}
                      <span className="shrink-0 font-medium text-ink-muted">Options</span>
                      <span className="ml-auto truncate text-ink-dim">
                        {participantLimit} people ·{' '}
                        {recordingMode === 'none'
                          ? 'not recorded'
                          : recordingMode === 'audio'
                            ? 'audio + transcript'
                            : VIDEO_MODE_SUMMARY[videoMode]}
                      </span>
                      <ChevronDown
                        aria-hidden
                        className={cn(
                          'size-4 shrink-0 text-ink-dim transition-transform duration-150',
                          optionsOpen && 'rotate-180',
                        )}
                      />
                    </button>

                    <div
                      id="call-options"
                      className={cn(
                        'space-y-2.5 sm:space-y-5',
                        optionsOpen ? 'block' : 'hidden sm:block',
                      )}
                    >
                      <Field label="What is this call about?" hint="Optional">
                        <Input
                          value={title}
                          onChange={(event) => setTitle(event.target.value)}
                          placeholder="Weekly check-in"
                          maxLength={120}
                          className="h-11"
                        />
                      </Field>

                      <div className="grid gap-3 sm:grid-cols-2 sm:gap-5">
                        <div className="space-y-2">
                          <Label>Room size</Label>
                          <Segmented
                            label="Room size"
                            size="sm"
                            value={String(participantLimit)}
                            onChange={(next) =>
                              setParticipantLimit(Number(next) as ParticipantLimit)
                            }
                            options={PARTICIPANT_LIMITS.map((limit) => ({
                              value: String(limit),
                              label: String(limit),
                            }))}
                          />
                          {/* Not a free number: `participant_limit` is an enum of
                              2/4/8 upstream and selects the P2/P4/P8 capacity
                              reservation profile. Three is a 422, not a rounding.

                              Hidden below `sm` because the control it explains
                              is three buttons reading 2, 4, 8 — the constraint
                              is visible in the widget. The sentence is worth
                              two lines of a desktop card and is not worth 6% of
                              an iPhone SE. */}
                          <p className="hidden text-xs text-ink-dim sm:block">
                            Reserves a capacity profile, so it is 2, 4 or 8 — not a maximum you can
                            nudge.
                          </p>
                        </div>

                        <div className="space-y-2">
                          <Label>Record this call</Label>
                          <Segmented
                            label="What this call keeps"
                            size="sm"
                            value={recordingMode}
                            onChange={(next) =>
                              setRecordingMode(next as RecordingSelection['mode'])
                            }
                            options={[
                              { value: 'none', label: 'None' },
                              { value: 'audio', label: 'Audio' },
                              { value: 'video', label: 'Video' },
                            ]}
                          />
                          <p className="hidden text-xs text-ink-dim sm:block">
                            Video includes audio, transcription, and the same post-call metrics.
                            The creator can change this mode during the call.
                          </p>
                        </div>

                        {/*
                         * THE RETAINED-VIDEO CHOICE, MADE BEFORE ANYONE JOINS.
                         *
                         * This screen argued for a year of commits that the
                         * choice belonged mid-call and nowhere else. The
                         * operator used the app and asked for it here. What
                         * survived the reversal is the part that was actually
                         * right, and it is load-bearing:
                         *
                         *   - The DEFAULT IS STILL OFF. Turning video on is an
                         *     affirmative act, and the request says so
                         *     explicitly rather than relying on a server
                         *     default (`recordingMode`).
                         *   - Every mode carries its real behaviour, because
                         *     these five differ in ways that are invisible
                         *     until playback.
                         *   - "One guest" is present and DISABLED. It names a
                         *     participant by identity, and at creation there is
                         *     no participant and no identity to name — the BFF
                         *     refuses it on this route
                         *     (`focus_identity_unavailable_at_creation`) rather
                         *     than trusting this select to be the only fence.
                         *
                         * `sm:col-span-2`: it is the widest of the three
                         * settings and the only one whose value needs a
                         * sentence to be honest, so it gets the full row under
                         * the two segmented controls instead of being squeezed
                         * beside one.
                         */}
                        {recordingMode === 'video' ? (
                        <div className="space-y-2 sm:col-span-2">
                          <Label htmlFor="video-retention">Recorded video view</Label>
                          {/* A native <select>, not a Segmented like its two
                              neighbours. Four options whose labels run to
                              "Active speaker" do not fit a segmented control on
                              a 375px phone at a legible size, and the native
                              picker gives the disabled fifth option somewhere
                              to be read. `styles.css` floors every select at
                              16px on a touch device, so this does not trigger
                              iOS's zoom-and-never-zoom-back. */}
                          <div className="relative">
                            <select
                              id="video-retention"
                              value={videoMode}
                              onChange={(event) =>
                                setVideoMode(event.target.value as CreationVideoView)
                              }
                              className={cn(
                                // `h-11` and NOT `tap44`. The utility works by
                                // growing an `::after` to 44px, and a <select>
                                // is a replaced element whose pseudo-elements
                                // browsers are free not to render — so the box
                                // is made 44px tall for real instead of being
                                // padded by something that may not exist.
                                'h-11 w-full appearance-none rounded-md border border-line-strong bg-surface',
                                'px-3 pr-9 text-sm text-ink',
                                'transition-[border-color,box-shadow] focus:border-accent focus:outline-none',
                                'focus-visible:ring-2 focus-visible:ring-accent/25',
                              )}
                            >
                              {CREATION_VIDEO_VIEW_OPTIONS.map((mode) => (
                                <option
                                  key={mode}
                                  value={mode}
                                  disabled={!CREATION_VIDEO_MODES.includes(mode)}
                                >
                                  {videoModeLabel(mode)}
                                  {CREATION_VIDEO_MODES.includes(mode)
                                    ? ''
                                    : ' — during the call only'}
                                </option>
                              ))}
                            </select>
                            <ChevronDown
                              aria-hidden
                              className="pointer-events-none absolute top-1/2 right-3 size-4 -translate-y-1/2 text-ink-dim"
                            />
                          </div>
                          {/* Shown at EVERY width, unlike the two help lines
                              above it. Those explain a control whose constraint
                              is visible on its own face (2, 4, 8 — Yes, No);
                              this one is the only thing on the screen that says
                              what the selected mode will actually do to the
                              recording, and dropping it on a phone would leave
                              the phone as the device where that is a mystery. */}
                          <p className="text-xs leading-relaxed text-ink-dim">
                            {VIDEO_MODE_DETAIL[videoMode]}
                          </p>
                          <p className="hidden text-xs leading-relaxed text-ink-dim sm:block">
                            The creator can change this during the call too, and that is where
                            “{videoModeLabel('focused_guest')}” lives — it keeps one named person’s
                            camera, and there is nobody to name until somebody joins.
                          </p>
                        </div>
                        ) : null}
                      </div>
                    </div>
                  </>
                ) : (
                  /* The label row — "Join code" and "Six characters. Case does
                     not matter." — is `sm` and up. On a phone the segmented
                     control directly above already reads "Join with a code",
                     and the field is a centred, letter-spaced mono box showing
                     `K7M-2QX`, which says both things the label says. It stays
                     in the DOM (and so in the accessible name) at every width;
                     only its box is dropped, which is 21px of a viewport where
                     21px decides whether the button below is on screen.

                     `&>div` is Field's label row and nothing else — the control
                     it wraps is an <input>, not a div. */
                  <Field
                    label="Join code"
                    hint="Six characters. Case does not matter."
                    htmlFor="join-code"
                    className="[&>div]:hidden sm:[&>div]:flex"
                  >
                    <Input
                      id="join-code"
                      value={code}
                      onChange={(event) => setCode(event.target.value)}
                      placeholder="K7M-2QX"
                      // A little longer than a code, because people paste the
                      // whole link and `extractCode` pulls the code back out.
                      maxLength={120}
                      autoCapitalize="characters"
                      spellCheck={false}
                      className={cn(
                        'code-display h-11 text-center text-lg uppercase sm:h-12',
                        code.length > 0 && !normalizedCode && 'border-critical',
                      )}
                    />
                  </Field>
                )}

                {failure ? (
                  <FailureNote
                    failure={failure}
                    countdown={countdown}
                    onRetry={() => {
                      setCountdown(null)
                      void (mode === 'create' ? createRoom() : joinRoom())
                    }}
                    // The one-tap way out of a recorder-capacity failure: send
                    // the same room without the part that was unavailable. The
                    // picker moves with it, so the screen never claims a mode
                    // that is not what was asked for.
                    onStartWithoutVideo={() => {
                      setCountdown(null)
                      setRecordingMode('audio')
                      void createRoom('audio')
                    }}
                  />
                ) : null}

                <button
                  ref={submitRef}
                  type="submit"
                  disabled={!canSubmit}
                  className={cn(
                    'inline-flex h-12 w-full items-center justify-center gap-2 rounded-lg text-base font-medium',
                    'bg-accent text-accent-fg shadow-sm transition-[filter,opacity] hover:brightness-110',
                    // Tap feedback is a component's job now: the shell turned
                    // `-webkit-tap-highlight-color` off globally because it
                    // painted square corners on rounded controls.
                    'active:brightness-95',
                    'disabled:pointer-events-none disabled:opacity-45',
                  )}
                >
                  {busy || countdown !== null ? (
                    <>
                      <Loader2 className="size-4 animate-spin" />
                      {mode === 'create' ? 'Creating the room…' : 'Joining…'}
                    </>
                  ) : mode === 'create' ? (
                    <>
                      <Sparkles className="size-4" />
                      Create the room
                      <ArrowRight className="size-4" />
                    </>
                  ) : (
                    <>
                      <Users className="size-4" />
                      Join
                      <ArrowRight className="size-4" />
                    </>
                  )}
                </button>

                {/* The host-gated alternative, deliberately secondary.
                    "Create the room" above is the right default for the common
                    case — somebody is here now and wants to talk now. This is
                    for the case the handoff spec is built around: the meeting
                    is later, the link has to be sent first, and no capacity
                    should be spent until the host actually arrives.

                    A text button rather than a second filled one, because two
                    equal-weight primary actions make the screen ask which kind
                    of user you are before it asks your name. It also costs the
                    phone layout one 20px line rather than a 48px control,
                    which the viewport arithmetic at the top of this file has
                    no room for. */}
                {mode === 'create' ? (
                  <button
                    type="button"
                    disabled={!trimmedName || busy || countdown !== null}
                    onClick={() => void createAppointment()}
                    className={cn(
                      // A REAL 44px target, not a 16px line of text.
                      //
                      // These two controls do opposite things and one of them
                      // spends money: "Create the room" above places a room
                      // upstream immediately, this one places nothing. When
                      // this was a bare 16px link 8px under a 48px button, a
                      // tap aimed slightly high did not miss — it hit the
                      // button, and the miss cost a real room, a capacity unit
                      // and a recorder reservation that nobody asked for.
                      //
                      // So: full-width flex row at h-11 for the hit area, and
                      // mt-1 rather than the form's gap so the gap between the
                      // two is deliberate rather than inherited.
                      'mt-1 flex h-11 w-full items-center justify-center rounded-lg text-xs',
                      'text-ink-muted underline underline-offset-4',
                      'transition-colors hover:text-ink hover:bg-surface-sunken/60',
                      'disabled:pointer-events-none disabled:opacity-45',
                    )}
                  >
                    Or get a link to start later
                  </button>
                ) : null}
              </form>

              {/* iPhone Safari only, and only until it is dismissed or seen
                  three times — it renders nothing at all anywhere else, so the
                  measurements above are unaffected for every other device.

                  THIS SCREEN, and not the join door where it used to live
                  alone. The door is unreachable from here: `enter()` stashes
                  the grant before navigating, so `/call/$code` opens straight
                  into the live call and its door phase never renders. An
                  iPhone user who starts a call therefore never met the hint,
                  which is the whole bug. See the mount-site note in
                  `lib/immersive-call.tsx`.

                  Directly under the primary button because that is where the
                  eye already is a moment before the call starts, and because
                  the advice costs a trip to Safari's Share sheet — free here,
                  and a dropped call anywhere later.

                  It is the only thing on this screen that can make the phone
                  layout taller than one viewport. That is a deliberate trade
                  against FINDINGS #24: the shell's `<main>` is the app's one
                  scroll container and absorbs it, and the alternative measured
                  today is that nobody on an iPhone ever learns the app can be
                  full screen. */}
              <AddToHomeScreenHint variant="inline" />
            </CardContent>
          </Card>

          {/* `sm` and up. The shell's footer carries this same claim on mobile,
              and a card that costs ~120px of a 563px viewport to repeat the
              sentence 60px below it is the difference between this screen
              fitting and not. */}
          <Card className="hidden border-accent-line/60 bg-accent-soft/40 sm:block">
            <CardContent className="flex gap-3 px-6 py-5">
              <Waves className="mt-0.5 size-4 shrink-0 text-accent" />
              {/* REWRITTEN when the video picker landed, because the old
                  sentence — "video is carried normally and never stored… that
                  is a property of this platform, not a setting on this page" —
                  became false on the same screen that states it. A privacy
                  claim that is contradicted by a control 200px above it is
                  worse than no claim at all. What is still true, and is the
                  part worth saying, is that video is kept only when somebody
                  asks for it. */}
              <p className="text-[0.8125rem] leading-relaxed text-ink-muted">
                Per-speaker audio, transcripts and conversation metrics are what a call keeps by
                default. Video is carried normally and is stored only if it is chosen — above,
                before anyone joins, or by the creator during the call.
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  )
}

/* -------------------------------------------------------------------------- */

function Segmented<T extends string>({
  value,
  onChange,
  options,
  size = 'md',
  label,
}: {
  value: T
  onChange: (value: T) => void
  options: { value: T; label: string }[]
  size?: 'sm' | 'md'
  /**
   * What this group of choices IS.
   *
   * Required rather than optional: without it a screen reader announces
   * "None, tab, selected, 1 of 3" with nothing saying what is being chosen,
   * and the visible heading beside it is a plain <Label> with no `htmlFor` to
   * borrow — a `<div role="tablist">` is not labelable that way. Making it a
   * required prop means a new group cannot be added unnamed.
   */
  label: string
}) {
  return (
    <div
      role="tablist"
      aria-label={label}
      className={cn(
        'grid gap-1 rounded-lg bg-surface-2 p-1',
        size === 'sm' ? 'text-[0.8125rem]' : 'text-sm',
      )}
      style={{ gridTemplateColumns: `repeat(${options.length}, minmax(0, 1fr))` }}
    >
      {options.map((option) => {
        const selected = option.value === value
        return (
          <button
            key={option.value}
            type="button"
            role="tab"
            aria-selected={selected}
            onClick={() => onChange(option.value)}
            className={cn(
              // `py-2.5` below `sm` is not decoration: it takes the tab from a
              // 36px painted box to 40px. 44 is the documented minimum, but a
              // segmented control that is 44 TALL per option makes this card
              // 8px taller than the viewport it has to fit in — so the box
              // stays at 40 and `tap44` centres a 44px touch area on it. The
              // control looks identical and the thumb gets the full target;
              // the parent has no overflow clipping, so nothing eats it.
              'tap44 rounded-md py-2.5 font-medium transition-[background-color,color,box-shadow] duration-150 sm:py-2',
              selected
                ? 'bg-surface text-ink shadow-[0_1px_2px_rgb(27_24_21/0.08)]'
                : 'text-ink-muted hover:text-ink',
            )}
          >
            {option.label}
          </button>
        )
      })}
    </div>
  )
}

function FailureNote({
  failure,
  countdown,
  onRetry,
  onStartWithoutVideo,
}: {
  failure: Failure
  countdown: number | null
  onRetry: () => void
  onStartWithoutVideo?: () => void
}) {
  return (
    <div
      className={cn(
        'rounded-lg border p-4',
        failure.retryable
          ? 'border-warn/30 bg-warn-soft/60'
          : 'border-critical/30 bg-critical-soft/60',
      )}
    >
      <p
        className={cn(
          'text-[0.8125rem] font-medium',
          failure.retryable ? 'text-warn' : 'text-critical',
        )}
      >
        {failure.title}
      </p>
      <p className="mt-1 text-[0.8125rem] leading-relaxed text-ink-muted">{failure.detail}</p>
      {/* The countdown and the escape hatch are siblings, not alternatives.
          A recorder-capacity failure is retrying itself on a timer AND is the
          one failure the person can settle immediately by changing what they
          asked for; hiding the second behind the first would make them wait
          out a retry to reach it. */}
      <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1">
        {/* "Try again" ONLY when trying again could work.
        
            A closed room and a bad code are terminal: the sentence above
            already says so ("A code stops working when its room closes"), and
            then offering retry as the only action contradicted it and
            reproduced the identical 409 for as long as anyone kept clicking.
            `retryable` is the server's own classification, carried through the
            error envelope for exactly this decision. */}
        {!failure.retryable ? null : countdown !== null ? (
          <p className="tabular text-[0.8125rem] text-ink-muted">Trying again in {countdown}…</p>
        ) : (
          <button
            type="button"
            onClick={onRetry}
            className="tap44 text-[0.8125rem] font-medium text-accent underline underline-offset-2"
          >
            Try again now
          </button>
        )}
        {failure.videoCaused && onStartWithoutVideo ? (
          <button
            type="button"
            onClick={onStartWithoutVideo}
            className="tap44 text-[0.8125rem] font-medium text-accent underline underline-offset-2"
          >
            Start without kept video
          </button>
        ) : null}
      </div>
    </div>
  )
}

/* -------------------------------------------------------------------------- */

/**
 * People paste the whole link. Pulling the code out of it is two lines here and
 * is otherwise a support conversation.
 */
function extractCode(input: string): string {
  const match = input.match(/\/call\/([^/?#\s]+)/i)
  return match?.[1] ?? input
}

function describeCreateFailure(
  cause: unknown,
  limit: ParticipantLimit,
  video: VideoRecordingMode,
): Failure {
  if (!(cause instanceof ApiError)) {
    return { title: 'Could not create the room', detail: 'An unexpected error.', retryable: false }
  }
  /*
   * BEFORE the generic 503, and matched on the CODE rather than the status,
   * because the two 503s this route can answer are different scarcities with
   * different answers. "Every node is busy" would be actively wrong here: every
   * media node may have been free, and what was missing was a recorder to keep
   * the video that was asked for.
   *
   * It is worth its own branch for one reason beyond accuracy — it is the only
   * creation failure the person can settle themselves, by asking for less. So
   * it names the cause, says the room was not half-made, and offers the change.
   */
  if (cause.code === 'no_video_recorder_capacity') {
    return {
      title: 'No video recorder is free right now',
      detail: `The call itself was fine — what was unavailable is a recorder to keep the video (“${videoModeLabel(video)}”). Nothing was created, so nothing is half-made, and starting without kept video works this second.`,
      // Read from the response, not asserted. The contract for this code says
      // retryable and the BFF forwards whatever the control plane sent; if it
      // ever sends false, this card must stop counting down rather than
      // insisting. The escape hatch below is offered either way, because it
      // does not depend on the fleet changing its mind.
      retryable: cause.retryable,
      videoCaused: true,
    }
  }
  if (cause.noCapacity) {
    return {
      title: 'Every node is busy',
      detail:
        limit > 2
          ? `No media node had capacity for a room of ${limit}. Larger rooms need a node with that capacity profile accepted, so a room of 2 may work when this does not.`
          : 'No media node had capacity for a new room. This is transient — placement is a server decision and there is no client-side node to fall back to.',
      retryable: true,
    }
  }
  if (cause.notImplemented) {
    return {
      title: 'This server cannot create rooms yet',
      detail: `${cause.message} — the route is declared but has no implementation in this build.`,
      retryable: false,
    }
  }
  if (cause.status === 0) {
    return {
      title: 'The server is not responding',
      detail: 'This app could not reach its own backend. Nothing was created.',
      retryable: false,
    }
  }
  return { title: 'Could not create the room', detail: cause.message, retryable: false }
}

function describeJoinFailure(cause: unknown): Failure {
  if (!(cause instanceof ApiError)) {
    return { title: 'Could not join', detail: 'An unexpected error.', retryable: false }
  }
  if (cause.status === 404) {
    return {
      title: 'No room with that code',
      detail: 'Check the six characters, or ask for a new code.',
      retryable: false,
    }
  }
  if (cause.status === 409 || cause.status === 410) {
    return {
      title: 'That room has ended',
      detail: 'A code stops working when its room closes.',
      retryable: false,
    }
  }
  if (cause.noCapacity) {
    return {
      title: 'Every node is busy',
      detail: 'The room exists but the fleet could not take you right now. Try again in a moment.',
      retryable: true,
    }
  }
  if (cause.notImplemented) {
    return {
      title: 'This server cannot join rooms yet',
      detail: `${cause.message} — the route is declared but has no implementation in this build.`,
      retryable: false,
    }
  }
  return { title: 'Could not join', detail: cause.message, retryable: false }
}
